import { InnerCrudList } from "@/shared/components";
import { InnerCrudCreateDialog } from "./InnerCrudCreateDialog";
import { InnerCrudEditDialog } from "./InnerCrudEditDialog";
import { useSnackbar } from "notistack";
import { InnerCrudContextProvider } from "./InnerCrudContext";
import { useSearchParams } from "react-router-dom";
import type { Schema } from "../Form/types";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import type {
  ColumnDefinition,
  CrudTexts,
  MutationVariables,
  TableData,
  CreateSchemaDefinition,
  EditSchemaDefinition,
  EditSchemaStep,
} from "./types";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

function getEditStep(editSchema: EditSchemaDefinition): EditSchemaStep {
  const def = editSchema as Record<string, EditSchemaStep>;
  return def.main ?? Object.values(def)[0];
}

const defaultTexts = (t: TFunction, texts: CrudTexts = {}) => ({
  filterTitle: "Filter",
  listTitle: "Items list",
  itemCreated: "Item created",
  itemUpdated: "Item updated",
  createItem: "Create item",
  refetchList: "Refetch list",
  deleteItems: "Delete items",
  itemsDeleted: "Items deleted",
  itemsRepeated: "Items repeated",
  itemsRestored: "Items restored",
  repeatItems: "Repeat items",
  cancel: "Cancel",
  editItem: "Edit item",
  showTitle: "Details",
  unsavedChangesWarning: t("shared.form.unsavedChangesWarning", {
    defaultValue:
      "У вас есть несохраненные изменения. Покинуть страницу без сохранения?",
  }),
  ...texts,
});

export interface InnerCRUDProps<Row extends { id: string } = any> {
  filterSchema?: Schema;
  filterQuery?: any; // GraphQL query
  filterQueryVariables?: MutationVariables;
  autoFilter?: boolean; // Автоматические фильтры без кнопки "Применить"
  listQuery?: any; // GraphQL query
  listPollInterval?: number;
  listQueryVariables?: MutationVariables;
  texts?: CrudTexts;
  getTableData: (data: any) => TableData<Row>;
  mapColumns: (row: Row) => any[];
  columns: ColumnDefinition<Row>[];
  download?: boolean;
  /** Показывать список карточками вместо таблицы (нужны mobileColumns) */
  useMobileView?: boolean;
  mobileColumns?: DataListViewColumns<Row>;
  /** Какие строки можно выбирать; у остальных чекбокс скрыт */
  hasShowSelect?: (row: Row) => boolean;
  /** Полностью заменяет набор кнопок над выбранными строками */
  selectableComponent?:
    | React.ReactNode
    | ((
        selectedIds: string[],
        onClearSelection?: () => void
      ) => React.ReactNode);
  createSchema?: CreateSchemaDefinition;
  createMutationVariables?: MutationVariables | (() => MutationVariables);
  createInitialVariables?: Partial<Row>;
  deleteMutation?: any; // GraphQL mutation
  deleteMutationVariables?: MutationVariables;
  repeatMutation?: any; // GraphQL mutation
  repeatMutationVariables?: MutationVariables;
  restoreMutation?: any; // GraphQL mutation
  editSchema?: EditSchemaDefinition;
  refetchQueries?: string[];
  getEditActions?: (args: { id: string }) => React.ReactNode;
  // Метаданные для передачи в поля без включения в данные формы
  metadata?: Record<string, any>;
}

export function InnerCRUD<Row extends { id: string } = any>({
  filterSchema,
  filterQuery,
  filterQueryVariables,
  autoFilter = true,

  listQuery,
  listPollInterval,
  listQueryVariables,

  texts,

  getTableData,
  mapColumns,
  columns,

  download,

  createSchema,
  createMutationVariables,
  createInitialVariables,

  deleteMutation,
  deleteMutationVariables,

  repeatMutation,
  repeatMutationVariables,

  restoreMutation,

  editSchema,

  refetchQueries = [],
  getEditActions,
  metadata,
  useMobileView,
  mobileColumns,
  hasShowSelect,
  selectableComponent,
}: InnerCRUDProps<Row>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const id = searchParams.get("innerId");
  const tab = searchParams.get("innerTab");

  const setTab = (tab?: string) => {
    if (tab) {
      searchParams.set("innerTab", tab);
    } else {
      searchParams.delete("innerTab");
    }
    setSearchParams(searchParams, { replace: true });
  };

  const setId = (id?: string) => {
    if (id) {
      searchParams.set("innerId", id);
    } else {
      searchParams.delete("innerId");
    }
    setSearchParams(searchParams, { replace: true });
  };

  const onSubmitCreateDialog = () => {
    setTab();
    enqueueSnackbar(texts?.itemCreated || "Item created", {
      variant: "success",
    });
  };

  const onSubmitUpdateDialog = () => {
    // setId();
    enqueueSnackbar(texts?.itemUpdated || "Item updated", {
      variant: "success",
    });
  };

  const onCloseCreateDialog = () => {
    setTab();
  };

  const onCloseEditDialog = () => {
    setId();
  };

  const onClickCreate = () => {
    setTab("create");
  };

  const listQueryName = listQuery.definitions.find(
    (definition: any) => definition.operation === "query"
  ).name.value;

  const _refetchQueries = refetchQueries.concat(listQueryName);

  const createResolved = createSchema
    ? {
        schema: createSchema.main.schema,
        createMutation: createSchema.main.createMutation,
        paper: createSchema.main.paper ?? true,
      }
    : null;

  const editResolved = editSchema
    ? (() => {
        const step = getEditStep(editSchema);
        return {
          schema: step.schema,
          editQuery: step.editQuery,
          updateMutation: step.updateMutation,
          getEditData: step.getEditData,
          paper: step.paper ?? true,
        };
      })()
    : null;

  return (
    <Box sx={{ backgroundColor: "background.default" }}>
      <InnerCrudContextProvider value={{ setId }}>
        <InnerCrudList
          filterSchema={filterSchema}
          filterQuery={filterQuery}
          filterQueryVariables={filterQueryVariables}
          listQuery={listQuery}
          listPollInterval={listPollInterval}
          listQueryVariables={listQueryVariables}
          texts={defaultTexts(t, texts)}
          getTableData={getTableData}
          mapColumns={mapColumns}
          columns={columns}
          download={download}
          createButton={!!createSchema}
          deleteMutation={deleteMutation}
          deleteMutationVariables={deleteMutationVariables}
          repeatMutation={repeatMutation}
          repeatMutationVariables={repeatMutationVariables}
          restoreMutation={restoreMutation}
          onClickCreate={onClickCreate}
          autoFilter={autoFilter}
          useMobileView={useMobileView}
          mobileColumns={mobileColumns}
          hasShowSelect={hasShowSelect}
          selectableComponent={selectableComponent}
        />

        {createResolved && tab === "create" && (
          <InnerCrudCreateDialog
            open
            onClose={onCloseCreateDialog}
            texts={defaultTexts(t, texts)}
            createSchema={createResolved.schema}
            createMutation={createResolved.createMutation}
            createMutationVariables={createMutationVariables}
            onSubmitSuccess={onSubmitCreateDialog}
            refetchQueries={_refetchQueries}
            createInitialVariables={createInitialVariables}
            metadata={metadata}
            paper={createResolved.paper}
          />
        )}

        {editResolved && id && (
          <InnerCrudEditDialog
            open={!!id}
            id={id}
            onClose={onCloseEditDialog}
            editQuery={editResolved.editQuery}
            editSchema={editResolved.schema}
            updateMutation={editResolved.updateMutation}
            onSubmitSuccess={onSubmitUpdateDialog}
            texts={defaultTexts(t, texts)}
            getEditData={editResolved.getEditData}
            refetchQueries={_refetchQueries}
            getEditActions={getEditActions}
            metadata={metadata}
            paper={editResolved.paper}
          />
        )}
      </InnerCrudContextProvider>
    </Box>
  );
}
